import {
  IconHandStars,
  IconHeart,
  IconLightbulb,
  IconSearch,
  IconThumbsUp,
  ProgressIndicator,
  Tooltip,
} from "@veneer/core";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../assets/css/views/home.css";
import { IReactionDto } from "../../models/dto/generalApi/IReactionDto";
import { ReactionEnum } from "../../models/enum/ReactionEnum";
import { Status } from "../../models/enum/Status";
import publisherService from "../../services/generalApi/publisher.service";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { decryptText } from "../../utils/encryptionUtils";
import { sanitizeUrl } from "../../utils/sanitizeText";
import { IPublisherDto } from "../../models/dto/generalApi/IPublisherDto";

interface IPublisherProps {
  publisher: IPublisherDto;
  updatePublisher: () => void;
}

const Publisher = ({ publisher, updatePublisher }: IPublisherProps) => {
  const [t] = useTranslation("global");
  const [frameHeight, setFrameHeight] = useState("0px");
  const [userId, setUserId] = useState(0);
  const iconRef = useRef(null);
  const state: any = useSelector((state: RootState) => state.user);

  const getYouTubeEmbedUrl = (url: string) => {
    const videoIdMatch = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/
    );

    if (videoIdMatch && videoIdMatch[1]) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }

    return null;
  };

  useEffect(() => {
    if (window.innerWidth > 992) {
      setFrameHeight(
        (window.innerWidth - window.innerWidth / 1.9).toString() + "px"
      );
    } else
      setFrameHeight(
        (window.innerWidth + window.innerWidth / 8).toString() + "px"
      );
  }, []);

  useEffect(() => {
    setUserId(Number(decryptText(state.user.userId)));
  }, []);

  const changeMyReaction = (
    reactionType: any,
    postId: any,
    statusId: number,
    userId: number
  ): void => {
    const reactionObject: IReactionDto = {
      reactionType: parseInt(reactionType, 10),
      postID: postId,
      statusID: statusId,
      userID: userId,
    };

    publisherService
      .postInsertReaction(reactionObject)
      .then((r) => {
        if (r === Status.updated) {
          updatePublisher();
        }
      })
      .catch((r) => console.error(r));
  };

  const renderReactionIcon = (
    reactionType: keyof typeof ReactionEnum,
    icon: JSX.Element,
    filledColor: string
  ) => {
    const isFilled = publisher.userReaction === ReactionEnum[reactionType];

    return (
      <Tooltip
        arrow
        content={<>{t(`header.reaction${reactionType}`)}</>}
        placement="bottom"
      >
        {React.cloneElement(icon, {
          color: isFilled ? filledColor : undefined,
          filled: isFilled,
          onClick: () =>
            changeMyReaction(
              ReactionEnum[reactionType],
              publisher.postID,
              isFilled ? 0 : 1,
              userId
            ),
          className: "postZoom",
          ref: !isFilled ? iconRef : undefined,
        })}
      </Tooltip>
    );
  };

  const renderContent = () => {
    switch (publisher.type) {
      case "image":
        return publisher.imageURL ? (
          <img
            src={sanitizeUrl(publisher.imageURL)}
            alt={publisher.postTitle}
          />
        ) : (
          <ProgressIndicator appearance="circular" behavior="indeterminate" />
        );
      case "video":
        const embedUrl = getYouTubeEmbedUrl(publisher.linkURL);
        return (
          <iframe
            height={frameHeight}
            width="100%"
            src={embedUrl || sanitizeUrl(publisher.linkURL)}
            title={publisher.postTitle}
          />
        );
      case "pdf":
        return publisher.fileURL ? (
          <iframe
            height={frameHeight}
            width="100%"
            src={sanitizeUrl(publisher.fileURL)}
            title={publisher.postTitle}
          />
        ) : (
          <ProgressIndicator appearance="circular" behavior="indeterminate" />
        );
      case "link":
        return (
          <>
            <iframe
              height={frameHeight}
              width="100%"
              src={sanitizeUrl(publisher.linkURL)}
              title={publisher.postTitle}
              style={{ marginTop: "10px" }}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ textAlign: "left", border: "0.5px solid black" }}>
      <div style={{ padding: "10px" }}>
        {publisher.postTitle && <b>{publisher.postTitle}</b>}
        <br />
        {publisher.postOwner && <label>{publisher.postOwner}</label>}
        <br />
        {publisher.linkURL && publisher.linkURL.length > 0 && (
          <a
            href={sanitizeUrl(publisher.linkURL)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {sanitizeUrl(publisher.linkURL)}
          </a>
        )}
        {renderContent()}
      </div>
      <div style={{ paddingLeft: "10px" }}>
        {renderReactionIcon("Recommend", <IconThumbsUp />, "colorBlue5")}
        {renderReactionIcon("Love", <IconHeart />, "colorRed5")}
        {renderReactionIcon("Curious", <IconSearch />, "colorPurple5")}
        {renderReactionIcon("Interesting", <IconLightbulb />, "colorYellow5")}
        {renderReactionIcon("Celebrate", <IconHandStars />, "colorGreen5")}
      </div>
      <div style={{ paddingLeft: "15px" }}>
        <b>
          {publisher.countReactions.toString()} {t("header.reactions")}
        </b>
      </div>
      <div style={{ paddingLeft: "15px", paddingBottom: "15px" }}>
        {publisher.postDescription && <p>{publisher.postDescription}</p>}
      </div>
    </div>
  );
};

export default Publisher;
